import { sdk } from '@needful-things-mvp/sdk';

import { hoursSince, relativeTime } from './api-format';
import { USE_MOCK } from './data-mode';
import { unwrap } from './http';
import { type Paginated, delay, paginate } from './mock';

export type MediaType = 'image' | 'video' | 'audio' | 'document';
export type MediaOrderKey = 'newest' | 'name' | 'size';

export interface MediaUsage {
  id: string;
  kind: 'project' | 'proposal';
  label: string;
  slug: string;
  role: string;
}

/** A minimal reference to an asset, for pickers and relations. */
export interface MediaRef {
  id: string;
  url: string;
}

export interface MediaItem {
  id: string;
  filename: string;
  url: string;
  title: string;
  alt: string;
  tags: string[];
  type: MediaType;
  mimeType: string;
  sizeBytes: number;
  usages: MediaUsage[];
  /** API `usageCount`; derived from usages in the mock. */
  usageCount: number;
  updated: string;
  updatedHours: number;
}

/** API shape from `GET /admin/media`. */
export type MediaItemDto = Omit<MediaItem, 'usageCount'>;

function mapMedia(dto: MediaItemDto): MediaItem {
  return { ...dto, usageCount: dto.usages.length };
}

export interface MediaInput {
  filename: string;
  url: string;
  title?: string;
  tags?: string[];
}

export interface MediaUpdate {
  title?: string;
  alt?: string;
  tags?: string[];
}

export interface ListMediaParams {
  search?: string;
  type?: 'all' | MediaType;
  order?: MediaOrderKey;
  page?: number;
  pageSize?: number;
}

export type PaginatedMedia = Paginated<MediaItem>;

export const MEDIA_PAGE_SIZE = 12;
export const MEDIA_PAGE_SIZE_OPTIONS = [12, 24, 48, 96];

export const mediaTypes: MediaType[] = ['image', 'video', 'audio', 'document'];

const extensions: Record<MediaType, string[]> = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif', 'heic', 'tiff'],
  video: ['mp4', 'mov', 'webm', 'mkv', 'avi', 'm4v', 'mpeg'],
  audio: ['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'],
  document: ['pdf', 'doc', 'docx', 'txt', 'md', 'csv', 'xls', 'xlsx', 'ppt', 'pptx'],
};

/** Detects the media type from a filename or URL. */
export function detectMediaType(value: string): MediaType {
  const extension = value.split('?')[0]?.split('.').pop()?.toLowerCase() ?? '';
  for (const type of mediaTypes) {
    if (extensions[type].includes(extension)) return type;
  }
  return 'document';
}

function mimeTypeFor(filename: string, type: MediaType): string {
  const extension = filename.split('.').pop()?.toLowerCase() ?? 'bin';
  return `${type}/${extension}`;
}

let db: MediaItemDto[] = [
  {
    id: 'bloom-hero',
    filename: 'bloom-hero.jpg',
    url: 'https://cdn.needfulthings.com/media/bloom-hero.jpg',
    title: 'Bloom hero',
    alt: 'Sculptural installation with soft daylight.',
    tags: ['hero', 'bloom'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 2_400_000,
    usages: [
      { id: 'u1', kind: 'project', label: 'Bloom', slug: 'bloom', role: 'Hero' },
      { id: 'u2', kind: 'proposal', label: 'Still Water', slug: 'still-water', role: 'Cover' },
    ],
    updated: '2h ago',
    updatedHours: 2,
  },
  {
    id: 'still-water-01',
    filename: 'still-water-01.jpg',
    url: 'https://cdn.needfulthings.com/media/still-water-01.jpg',
    title: 'Still Water - study 01',
    alt: 'Close-up of reflective water surface.',
    tags: ['bloom', 'paper-cities'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 1_800_000,
    usages: [
      { id: 'u3', kind: 'project', label: 'Bloom', slug: 'bloom', role: 'Gallery' },
      { id: 'u4', kind: 'project', label: 'Paper Cities', slug: 'paper-cities', role: 'Gallery' },
      { id: 'u5', kind: 'proposal', label: 'Still Water', slug: 'still-water', role: 'Gallery' },
    ],
    updated: '3h ago',
    updatedHours: 3,
  },
  {
    id: 'quarry-light',
    filename: 'quarry-light.jpg',
    url: 'https://cdn.needfulthings.com/media/quarry-light.jpg',
    title: 'Quarry Light',
    alt: 'Light cutting through carved stone.',
    tags: ['bloom'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 3_100_000,
    usages: [
      { id: 'u6', kind: 'project', label: 'Bloom', slug: 'bloom', role: 'Gallery' },
      { id: 'u7', kind: 'proposal', label: 'Quarry Light', slug: 'quarry-light', role: 'Cover' },
    ],
    updated: '5h ago',
    updatedHours: 5,
  },
  {
    id: 'salt-lines',
    filename: 'salt-lines.jpg',
    url: 'https://cdn.needfulthings.com/media/salt-lines.jpg',
    title: 'Salt Lines',
    alt: 'Corroded metal scored by salt.',
    tags: ['salt-and-iron'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 2_900_000,
    usages: [
      { id: 'u8', kind: 'project', label: 'Salt & Iron', slug: 'salt-and-iron', role: 'Gallery' },
      { id: 'u9', kind: 'proposal', label: 'Salt Lines', slug: 'salt-lines', role: 'Cover' },
    ],
    updated: '1d ago',
    updatedHours: 24,
  },
  {
    id: 'anvil-process',
    filename: 'anvil-process.mp4',
    url: 'https://cdn.needfulthings.com/media/anvil-process.mp4',
    title: 'Anvil - process film',
    alt: 'Studio footage of forging.',
    tags: ['salt-and-iron', 'process'],
    type: 'video',
    mimeType: 'video/mp4',
    sizeBytes: 18_400_000,
    usages: [
      { id: 'u10', kind: 'project', label: 'Salt & Iron', slug: 'salt-and-iron', role: 'Process' },
    ],
    updated: '1d ago',
    updatedHours: 26,
  },
  {
    id: 'ember-study',
    filename: 'ember-study.jpg',
    url: 'https://cdn.needfulthings.com/media/ember-study.jpg',
    title: 'Ember Study',
    alt: 'Charcoal and pigment study.',
    tags: ['ember-field'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 2_200_000,
    usages: [
      { id: 'u11', kind: 'project', label: 'Ember Field', slug: 'ember-field', role: 'Gallery' },
      { id: 'u12', kind: 'proposal', label: 'Ember Study', slug: 'ember-study', role: 'Cover' },
    ],
    updated: '2d ago',
    updatedHours: 48,
  },
  {
    id: 'ash-field',
    filename: 'ash-field.jpg',
    url: 'https://cdn.needfulthings.com/media/ash-field.jpg',
    title: 'Ash Field',
    alt: 'Scorched field in layered ash.',
    tags: ['ember-field'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 1_500_000,
    usages: [
      { id: 'u13', kind: 'project', label: 'Ember Field', slug: 'ember-field', role: 'Gallery' },
    ],
    updated: '3d ago',
    updatedHours: 72,
  },
  {
    id: 'concrete-seasons',
    filename: 'concrete-seasons.jpg',
    url: 'https://cdn.needfulthings.com/media/concrete-seasons.jpg',
    title: 'Concrete Seasons',
    alt: 'Concrete forms against winter light.',
    tags: ['concrete-seasons'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 3_600_000,
    usages: [
      {
        id: 'u14',
        kind: 'project',
        label: 'Concrete Seasons',
        slug: 'concrete-seasons',
        role: 'Cover',
      },
    ],
    updated: '4d ago',
    updatedHours: 96,
  },
  {
    id: 'tidal-mark',
    filename: 'tidal-mark.mp3',
    url: 'https://cdn.needfulthings.com/media/tidal-mark.mp3',
    title: 'Tidal Mark - ambient loop',
    alt: 'Recorded shore ambience.',
    tags: ['tidal', 'audio'],
    type: 'audio',
    mimeType: 'audio/mpeg',
    sizeBytes: 4_200_000,
    usages: [{ id: 'u15', kind: 'project', label: 'Tidal', slug: 'tidal', role: 'Hero' }],
    updated: '5d ago',
    updatedHours: 120,
  },
  {
    id: 'artist-statement',
    filename: 'artist-statement.pdf',
    url: 'https://cdn.needfulthings.com/media/artist-statement.pdf',
    title: 'Artist statement',
    alt: 'PDF artist statement.',
    tags: ['document'],
    type: 'document',
    mimeType: 'application/pdf',
    sizeBytes: 640_000,
    usages: [],
    updated: '1w ago',
    updatedHours: 168,
  },
  {
    id: 'paper-fold-01',
    filename: 'paper-fold-01.jpg',
    url: 'https://cdn.needfulthings.com/media/paper-fold-01.jpg',
    title: 'Paper Fold 01',
    alt: 'Folded paper study.',
    tags: ['paper-cities'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 1_450_000,
    usages: [],
    updated: '1w ago',
    updatedHours: 174,
  },
  {
    id: 'paper-fold-02',
    filename: 'paper-fold-02.jpg',
    url: 'https://cdn.needfulthings.com/media/paper-fold-02.jpg',
    title: 'Paper Fold 02',
    alt: 'Layered paper folds.',
    tags: ['paper-cities'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 1_620_000,
    usages: [],
    updated: '1w ago',
    updatedHours: 176,
  },
  {
    id: 'signal-bloom',
    filename: 'signal-bloom.jpg',
    url: 'https://cdn.needfulthings.com/media/signal-bloom.jpg',
    title: 'Signal Bloom',
    alt: 'Floral forms from signal noise.',
    tags: ['paper-cities'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 2_050_000,
    usages: [],
    updated: '1w ago',
    updatedHours: 178,
  },
  {
    id: 'iron-bloom',
    filename: 'iron-bloom.jpg',
    url: 'https://cdn.needfulthings.com/media/iron-bloom.jpg',
    title: 'Iron Bloom',
    alt: 'Forged metal petals.',
    tags: ['salt-and-iron'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 2_780_000,
    usages: [],
    updated: '1w ago',
    updatedHours: 180,
  },
  {
    id: 'glass-tide',
    filename: 'glass-tide.jpg',
    url: 'https://cdn.needfulthings.com/media/glass-tide.jpg',
    title: 'Glass Tide',
    alt: 'Blown glass catching low light.',
    tags: ['tidal'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 1_980_000,
    usages: [],
    updated: '2w ago',
    updatedHours: 340,
  },
  {
    id: 'wood-grain',
    filename: 'wood-grain.jpg',
    url: 'https://cdn.needfulthings.com/media/wood-grain.jpg',
    title: 'Wood Grain',
    alt: 'Eroded wood grain macro.',
    tags: ['tidal'],
    type: 'image',
    mimeType: 'image/jpeg',
    sizeBytes: 2_310_000,
    usages: [],
    updated: '2w ago',
    updatedHours: 344,
  },
];

function formatSize(bytes: number): string {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
}

export function mediaSizeLabel(item: MediaItem): string {
  return formatSize(item.sizeBytes);
}

/** Wire shape from the media endpoints (list adds `usageCount`, detail `usages`). */
interface MediaApiDto {
  id: string;
  url: string;
  type: MediaType;
  filename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  title: string | null;
  alt: string | null;
  tags: string[];
  updatedAt?: string;
  usageCount?: number;
  usages?: MediaUsage[];
}

function mapMediaDto(dto: MediaApiDto): MediaItem {
  const type = dto.type ?? detectMediaType(dto.filename ?? dto.url);
  return {
    id: dto.id,
    filename: dto.filename ?? '',
    url: dto.url,
    title: dto.title ?? dto.filename ?? '',
    alt: dto.alt ?? '',
    tags: dto.tags ?? [],
    type,
    mimeType: dto.mimeType ?? mimeTypeFor(dto.filename ?? '', type),
    sizeBytes: dto.sizeBytes ?? 0,
    usages: dto.usages ?? [],
    usageCount: dto.usageCount ?? dto.usages?.length ?? 0,
    updated: dto.updatedAt ? relativeTime(dto.updatedAt) : 'just now',
    updatedHours: dto.updatedAt ? hoursSince(dto.updatedAt) : 0,
  };
}

function sortMedia(items: MediaItemDto[], order: MediaOrderKey): MediaItemDto[] {
  const sorted = [...items];
  switch (order) {
    case 'name':
      sorted.sort((a, b) => a.filename.localeCompare(b.filename));
      break;
    case 'size':
      sorted.sort((a, b) => b.sizeBytes - a.sizeBytes);
      break;
    default:
      sorted.sort((a, b) => a.updatedHours - b.updatedHours);
  }
  return sorted;
}

export async function listMedia(params: ListMediaParams = {}): Promise<PaginatedMedia> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.media.get({
      query: {
        search: params.search,
        type: params.type && params.type !== 'all' ? params.type : undefined,
        order: params.order,
        page: params.page,
        pageSize: params.pageSize,
      },
    });
    const page = unwrap<Paginated<MediaApiDto>>(response);
    return { ...page, items: page.items.map(mapMediaDto) };
  }
  await delay();
  const { search, type = 'all', order = 'newest', page, pageSize = MEDIA_PAGE_SIZE } = params;

  const term = search?.trim().toLowerCase();
  const filtered = db.filter((item) => {
    const matchesType = type === 'all' || item.type === type;
    const matchesSearch =
      !term ||
      item.filename.toLowerCase().includes(term) ||
      item.title.toLowerCase().includes(term) ||
      item.tags.some((tag) => tag.toLowerCase().includes(term));
    return matchesType && matchesSearch;
  });

  const result = paginate(sortMedia(filtered, order), page, pageSize);
  return { ...result, items: result.items.map(mapMedia) };
}

export function queryMedia(params: ListMediaParams = {}): Promise<PaginatedMedia> {
  return listMedia(params);
}

export async function getMedia(id: string): Promise<MediaItem | undefined> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.media({ id }).get();
    return mapMediaDto(unwrap<MediaApiDto>(response));
  }
  await delay();
  const dto = db.find((item) => item.id === id);
  return dto ? mapMedia(dto) : undefined;
}

export async function createMedia(input: MediaInput): Promise<MediaItem> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.media.post({
      url: input.url,
      filename: input.filename,
      title: input.title,
      tags: input.tags,
    });
    return mapMediaDto(unwrap<MediaApiDto>(response));
  }
  await delay();
  const type = detectMediaType(input.filename);
  const item: MediaItemDto = {
    id: crypto.randomUUID(),
    filename: input.filename,
    url: input.url,
    title: input.title || input.filename,
    alt: '',
    tags: input.tags ?? [],
    type,
    mimeType: mimeTypeFor(input.filename, type),
    sizeBytes: Math.round(Math.random() * 5_000_000) + 100_000,
    usages: [],
    updated: 'just now',
    updatedHours: 0,
  };
  db = [item, ...db];
  return mapMedia(item);
}

export async function updateMedia(id: string, patch: MediaUpdate): Promise<MediaItem> {
  if (!USE_MOCK) {
    const response = await sdk.api.admin.media({ id }).patch({
      title: patch.title,
      alt: patch.alt,
      tags: patch.tags,
    });
    return mapMediaDto(unwrap<MediaApiDto>(response));
  }
  await delay();
  let updated: MediaItemDto | undefined;
  db = db.map((item) => {
    if (item.id !== id) return item;
    updated = {
      ...item,
      title: patch.title ?? item.title,
      alt: patch.alt ?? item.alt,
      tags: patch.tags ?? item.tags,
      updated: 'just now',
      updatedHours: 0,
    };
    return updated;
  });
  if (!updated) throw new Error(`Media ${id} not found`);
  return mapMedia(updated);
}

export async function deleteMedia(id: string): Promise<void> {
  if (!USE_MOCK) {
    await sdk.api.admin.media({ id }).delete();
    return;
  }
  await delay();
  db = db.filter((item) => item.id !== id);
}

/** A media row as returned by the API (`POST /admin/media`). */
interface MediaRecord {
  id: string;
  url: string;
  type: MediaType;
  filename: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  title: string | null;
  alt: string | null;
  tags: string[];
}

/** Signed upload target from `POST /admin/media/upload-url`. */
interface UploadTarget {
  key: string;
  uploadUrl: string;
  publicUrl: string;
  /** Headers that are part of the signature; must be sent with the PUT. */
  headers: Record<string, string>;
}

/**
 * Uploads a file to the media library: asks the API for a signed target, PUTs
 * the bytes straight to R2, then registers the asset. In mock mode the upload
 * stays in-memory (an object URL) so the CMS works without a backend.
 */
export async function uploadMediaAsset(file: File): Promise<MediaItem> {
  if (USE_MOCK) {
    return createMedia({
      filename: file.name,
      url: URL.createObjectURL(file),
      title: file.name,
    });
  }

  const contentType = file.type || 'application/octet-stream';
  const target = unwrap<UploadTarget>(
    await sdk.api.admin.media['upload-url'].post({
      filename: file.name,
      contentType,
      sizeBytes: file.size,
    }),
  );

  const upload = await fetch(target.uploadUrl, {
    method: 'PUT',
    headers: target.headers,
    body: file,
  });
  if (!upload.ok) throw new Error(`Upload failed (${upload.status})`);

  const record = unwrap<MediaRecord>(
    await sdk.api.admin.media.post({
      url: target.publicUrl,
      filename: file.name,
      mimeType: contentType,
      sizeBytes: file.size,
      storageKey: target.key,
    }),
  );

  return mapMediaDto(record);
}
